<template>
  <div class="sales-view container py-4">
    <!-- Header -->
    <div class="text-center mb-4">
      <h2 class="fw-bold mb-2">
        <font-awesome-icon icon="crown" class="me-2 text-warning" />
        Bereich-Upgrade
      </h2>
      <p class="text-muted mb-0">
        Erweitere deine Bereich-Funktionen mit individuellen Templates und erweiterten Verwaltungsoptionen
      </p>
    </div>

    <!-- Progress Steps -->
    <ProgressSteps
      :steps="steps"
      :current-step="currentStep"
      @go-to-step="goToStep"
    />

    <!-- Step Content -->
    <div class="card">
      <div class="card-body p-3">
        <!-- Step 1: Team Selection -->
        <TeamSelectionStep
          v-if="currentStep === 0"
          :available-teams="availableTeams"
          :selected-team="selectedTeam"
          :loading="loading"
          :error="error"
          @team-selected="selectTeam"
        />

        <!-- Step 2: Plan Selection -->
        <PlanSelectionStep
          v-if="currentStep === 1"
          :available-plans="availablePlans"
          :selected-team="selectedTeam"
          :selected-plan="selectedPlan"
          :coupon-result="couponResult"
          @plan-selected="selectPlan"
          @coupon-validated="onCouponValidated"
          @coupon-cleared="onCouponCleared"
        />

        <!-- Step 3: Billing Data -->
        <div v-if="currentStep === 2" class="sales-step-content">
          <div class="text-center mb-3">
            <font-awesome-icon icon="file-invoice" size="3x" class="text-info mb-2" />
            <h4>Rechnungsdaten</h4>
            <p class="text-muted mb-0">
              <span v-if="isFreeUpgrade">Rechnungsdaten</span>
              <span v-else>Bitte gib deine Rechnungsdaten ein</span>
            </p>
          </div>

          <BillingForm
            :billing-data="billingData"
            :is-free-upgrade="isFreeUpgrade"
            :show-tax-fields="true"
            :show-save-option="true"
            :show-card="true"
            @proceed-to-payment="proceedToPayment"
            @billing-data-changed="onBillingDataChanged"
          />
        </div>

        <!-- Step 4: Payment -->
        <OrderSummaryStep
          v-if="currentStep === 3"
          :selected-team="selectedTeam"
          :selected-plan="selectedPlan"
          :price-data="priceData"
          :billing-data="billingData"
          :has-filled-billing-data="hasFilledBillingData"
          :coupon-result="couponResult"
          :coupon-code="couponCode"
          :is-free-upgrade="isFreeUpgrade"
          @payment-method-changed="onPaymentMethodChanged"
          @terms-changed="onTermsChanged"
        />

        <!-- Navigation -->
        <div class="d-flex justify-content-between mt-4">
          <button 
            v-if="currentStep > 0"
            @click="previousStep" 
            class="btn btn-outline-secondary"
          >
            <font-awesome-icon icon="arrow-left" class="me-2" />
            Zurück
          </button>
          <div v-else></div>
          
          <button 
            v-if="currentStep < steps.length - 1"
            @click="nextStep" 
            class="btn btn-primary"
            :disabled="!canProceed"
          >
            Weiter
            <font-awesome-icon icon="arrow-right" class="ms-2" />
          </button>
          <button 
            v-else
            @click="processUpgrade" 
            class="btn btn-success"
            :disabled="!canProceed || !allTermsAccepted"
          >
            <font-awesome-icon icon="check" class="me-2" />
            Jetzt verbindlich kaufen
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useTeamsStore } from '@/stores/teams'
import { pricingApi } from '@/api/pricing'
import { teamService } from '@/api/teams'
import {
  showSuccess,
  showError,
} from '@/services/notification'

// Composables
import { useBillingData } from './composables/useBillingData'
import { useCoupon } from './composables/useCoupon'
import { usePricing } from './composables/usePricing'
import { useWizardSteps } from './composables/useWizardSteps'

// Components
import ProgressSteps from './components/ProgressSteps.vue'
import BillingForm from './components/BillingForm.vue'
import TeamSelectionStep from './sales/TeamSelectionStep.vue'
import PlanSelectionStep from './sales/PlanSelectionStep.vue'
import OrderSummaryStep from './sales/OrderSummaryStep.vue'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()

const loading = ref(true)
const error = ref(null)
const selectedTeam = ref(null)
const selectedPlan = ref(null)
const paymentMethod = ref('paypal')
const availablePlans = ref([])
const allTermsAccepted = ref(false)

const steps = [
  {
    title: 'Bereich wählen',
    description: 'Wähle den Bereich aus'
  },
  {
    title: 'Plan wählen',
    description: 'Wähle den passenden Plan'
  },
  {
    title: 'Rechnungsdaten',
    description: 'Gib deine Daten ein'
  },
  {
    title: 'Zahlung',
    description: 'Bestätige und bezahle'
  }
]

const availableTeams = ref([])

// Use composables
const { billingData, hasFilledBillingData, validateBillingData, loadLastBillingData, getBillingDataForApi } = useBillingData()
const { couponCode, couponLoading, couponResult, validateCoupon, getCouponDescription, onCouponCodeChange } = useCoupon()
const { priceData, formatCurrency, calculatePriceData } = usePricing()
const { currentStep, canNavigateToStep, goToStep, nextStep, previousStep, isStepActive, isStepCompleted, isStepDisabled } = useWizardSteps(steps)

const mapInvoiceDataToApi = (data = {}) => ({
  firstName: data.firstName || '',
  lastName: data.lastName || '',
  company: data.company || data.companyName || '',
  street: data.street || '',
  number: data.number || data.houseNumber || '',
  zip: data.zip || data.zipCode || data.postalCode || '',
  city: data.city || '',
  country: data.country || 'Deutschland',
  email: data.email || '',
  vatId: data.vatId || '',
  taxId: data.taxId || ''
})

const isFreeUpgrade = computed(() => {
  return couponResult.value?.success && 
         couponResult.value.data.coupon.type === 'free_team_pro' &&
         priceData.value.totalPrice === 0
})

const canProceed = computed(() => {
  switch (currentStep.value) {
    case 0:
      return selectedTeam.value !== null
    case 1:
      return selectedPlan.value !== null || (couponResult.value?.success && couponResult.value.data.coupon.type === 'free_team_pro')
    case 2:
      return isFreeUpgrade.value || validateBillingData()
    case 3:
      return true
    default:
      return false
  }
})

const selectTeam = (team) => {
  selectedTeam.value = team
  nextStep()
}

const selectPlan = (plan) => {
  selectedPlan.value = plan
  calculatePriceData(plan, couponResult.value)
  nextStep()
}

const proceedToPayment = () => {
  if (validateBillingData()) {
    nextStep()
  }
}

const onCouponValidated = (result) => {
  couponResult.value = result
  if (result?.success && result.data.coupon.type === 'free_team_pro') {
    calculatePriceData(selectedPlan.value, result)
  }
}

const onCouponCleared = () => {
  couponResult.value = null
  if (selectedPlan.value) {
    calculatePriceData(selectedPlan.value, null)
  }
}

const getPlanTypeFromCoupon = () => {
  const couponPlan = couponResult.value?.success ? couponResult.value.data.coupon?.teamProPlan : null
  if (!couponPlan) return null
  return couponPlan === 'ultimate' ? 'team_ultimate' : 'team_pro'
}

const ensurePlanSelectionFromCoupon = () => {
  if (selectedPlan.value) return
  const planType = getPlanTypeFromCoupon()
  if (!planType) return

  const matchingPlan = availablePlans.value.find(plan => plan.type === planType)
  if (matchingPlan) {
    selectedPlan.value = matchingPlan
    calculatePriceData(matchingPlan, couponResult.value)
  }
}

watch([availablePlans, couponResult], ensurePlanSelectionFromCoupon, { immediate: true })

watch(selectedPlan, (plan) => {
  if (plan) {
    calculatePriceData(plan, couponResult.value)
  }
})

const onPaymentMethodChanged = (method) => {
  paymentMethod.value = method
}

const onTermsChanged = (accepted) => {
  allTermsAccepted.value = accepted
}

const onBillingDataChanged = (newBillingData) => {
  // Update billing data when form changes
  billingData.value = { ...billingData.value, ...newBillingData }
}

const loadTeamData = async () => {
  try {
    const { teamId } = route.query
    
    // Try eligible teams API first
    let eligibleTeams = []
    try {
      const response = await teamService.getEligibleTeamsForUpgrade()
      eligibleTeams = response.eligibleTeams || []
    } catch (apiError) {
      console.warn('Eligible teams API failed, falling back to regular teams:', apiError)
      // Fallback: Load all teams and filter for free ones
      const response = await teamService.getTeams(1, 100, '')
      eligibleTeams = (response.teams || []).filter(team => team.status === 'free')
    }
    
    // If a specific teamId is provided, ensure it's included even if not eligible
    if (teamId) {
      const specificTeam = eligibleTeams.find(t => t.id === teamId)
      if (!specificTeam) {
        try {
          const teamData = await teamService.getTeam(teamId)
          if (teamData && teamData.status === 'free') {
            eligibleTeams.unshift(teamData)
          }
        } catch (error) {
          console.warn(`Could not load specific team ${teamId}:`, error)
        }
      }
    }
    
    // Enrich teams with additional data if needed
    const enrichedTeams = await Promise.all(
      eligibleTeams.map(async (team) => {
        try {
          // Try to get full team data for statistics
          const fullTeamData = await teamService.getTeam(team.id)
          return {
            ...team,
            memberCount: fullTeamData.memberCount || 0,
            receiptCount: fullTeamData.receiptCount || 0,
            coins: fullTeamData.coins || 0,
            description: fullTeamData.description || team.description
          }
        } catch (error) {
          console.warn(`Could not load full data for team ${team.id}:`, error)
          return {
            ...team,
            memberCount: 0,
            receiptCount: 0,
            coins: 0,
            description: team.description || ''
          }
        }
      })
    )
    
    availableTeams.value = enrichedTeams
  } catch (error) {
    console.error('Error loading teams:', error)
    error.value = 'Bereiche konnten nicht geladen werden'
  } finally {
    loading.value = false
  }
}

const loadTeamPlanFeatures = async () => {
  try {
    const plans = await pricingApi.getTeamPlanFeatures()
    availablePlans.value = plans || []
  } catch (error) {
    console.warn('Team-Plan Features konnten nicht geladen werden:', error)
    availablePlans.value = []
  }
}

const processUpgrade = async () => {
  try {
    if (!allTermsAccepted.value) {
      showError('Bitte akzeptiere alle Bedingungen, um fortzufahren.')
      return
    }

    const planType = selectedPlan.value?.type || getPlanTypeFromCoupon()
    if (!planType) {
      showError('Bitte wähle einen Plan oder verwende einen gültigen Gutschein.')
      return
    }

    // Process upgrade for selected team
    if (selectedTeam.value?.status === 'free') {
      const finalPaymentMethod = priceData.value.totalPrice > 0 ? paymentMethod.value : 'free'
      const finalCouponCode = couponCode.value?.trim() || null
      await handleTeamUpgrade(selectedTeam.value.id, planType, finalPaymentMethod, finalCouponCode)
    }
    
    showSuccess('Upgrade erfolgreich durchgeführt!')
    router.push('/teams')
  } catch (error) {
    console.error('Upgrade error:', error)
    showError('Fehler beim Upgrade. Bitte versuche es erneut.')
  }
}

const handleTeamUpgrade = async (teamId, planType, paymentMethod, couponCode = null) => {
  try {
    let lastBillingData = null
    
    // For free upgrades with coupon, use filled billing data if available, otherwise empty object
    if (paymentMethod === 'free' && couponCode) {
      // Check if billing data is filled
      const hasBillingData = billingData.value.firstName && 
                            billingData.value.lastName && 
                            billingData.value.street && 
                            billingData.value.number && 
                            billingData.value.zip && 
                            billingData.value.city && 
                            billingData.value.country && 
                            billingData.value.email
      
      if (hasBillingData) {
        lastBillingData = getBillingDataForApi()
      } else {
        // Use empty object like in notification.js
        lastBillingData = {}
      }
    } else if (paymentMethod !== 'free') {
      // For paid upgrades, use current form data if saveForFuture is true, otherwise load from API
      if (billingData.value.saveForFuture && hasFilledBillingData.value) {
        lastBillingData = getBillingDataForApi()
      } else {
        try {
          const { getInvoiceData } = await import('@/api/invoiceData')
          const response = await getInvoiceData()
          if (response.success && response.data.invoiceData) {
            lastBillingData = mapInvoiceDataToApi(response.data.invoiceData)
          }
        } catch (error) {
          console.warn('Konnte Rechnungsdaten nicht laden:', error)
        }
      }
    }

    // Use the same structure as TeamsUpgradeView
    const result = await teamService.upgradeTeam(
      teamId,
      planType,
      lastBillingData,
      couponCode || null,
      paymentMethod,
    )

    if (result.requiresPayment && result.paypalOrder) {
      showSuccess('PayPal-Zahlung wird vorbereitet...')
      window.open(result.paypalOrder.approvalUrl || result.paypalOrder.redirectUrl, '_self', '')
    } else {
      // Save billing data if checkbox is checked
      if (billingData.value.saveForFuture && hasFilledBillingData.value) {
        try {
          const { saveInvoiceData } = await import('@/api/invoiceData')
          await saveInvoiceData(getBillingDataForApi())
        } catch (error) {
          console.warn('Konnte Rechnungsdaten nicht speichern:', error)
        }
      }
      
      if (paymentMethod === 'free' || couponCode) {
        showSuccess(`Bereich erfolgreich auf ${planType === 'team_pro' ? 'Pro' : 'Ultimate'} Plan upgegradet! Das kostenlose Upgrade wurde erfolgreich angewendet.`)
      } else if (paymentMethod === 'bank_transfer') {
        showSuccess(`Bereich erfolgreich auf ${planType === 'team_pro' ? 'Pro' : 'Ultimate'} Plan upgegradet! Die Zahlungsaufforderung wurde per E-Mail versendet.`)
      } else {
        showSuccess(`Bereich erfolgreich auf ${planType === 'team_pro' ? 'Pro' : 'Ultimate'} Plan upgegradet!`)
      }
    }
  } catch (error) {
    console.error('Team upgrade error:', error)
    throw error
  }
}

onMounted(async () => {
  await loadTeamData()
  await loadTeamPlanFeatures()
  await loadLastBillingData()
  
  // Handle query parameters from TeamsUpgradeView
  const { teamId, planType, couponCode: queryCouponCode, coupon } = route.query
  
  if (teamId) {
    // Pre-select team if provided
    const team = availableTeams.value.find(t => t.id === teamId)
    if (team) {
      selectedTeam.value = team
      currentStep.value = 1 // Skip to plan selection
    }
  }
  
  if (planType) {
    // Pre-select plan if provided
    const plan = availablePlans.value.find(p => p.type === planType)
    if (plan) {
      selectedPlan.value = plan
      currentStep.value = 2 // Skip to billing data
    }
  }
  
  // Handle both couponCode and coupon parameters
  const finalCouponCode = queryCouponCode || coupon
  if (finalCouponCode) {
    // Pre-fill coupon code if provided
    couponCode.value = finalCouponCode
    // Auto-validate coupon if provided
    if (finalCouponCode.trim()) {
      await validateCoupon('team_upgrade')
    }
  }
})
</script>

<style scoped>
/* Additional styles can be added here if needed */
</style>
